/* eslint-disable @typescript-eslint/no-explicit-any */
import React, {useEffect, useState, useMemo} from 'react';
import * as Yup from 'yup';
import {Formik} from 'formik';
import {Helmet} from 'react-helmet-async';
import {useParams, useNavigate, useLocation} from 'react-router-dom';
import {Box, CardContent, Checkbox, FormControl, Grid} from '@mui/material';
import RichTextEditor from '../../../components/RichTextEditor/RichTextEditor';
import {stateToHTML} from 'draft-js-export-html';
import {convertFromHTML, ContentState, convertToRaw} from 'draft-js';
import SourceOutlinedIcon from '@mui/icons-material/SourceOutlined';

import AllModal from './VoucherDetail.method';
import {path} from '../../../routes/path';
import {images} from '../../../constants/image';
import useAuth from '../../../hooks/useAuth';
import {DataDetail, typeRoutes} from '../../../types/typeVoucher';
import VoucherService from '../../../services/Voucher/voucher.service';
import {
  Breadcrumbs,
  DateTimePicker,
  Input,
  TextArea,
  FooterDetail,
  RadioGroups,
  Stats,
  MultipleSelectCheckbox,
  Tooltips,
  AspectSelectImage,
} from '../../../components';
import queryString from 'query-string';
import {formatDateTime} from '../../../utils';

// style
import {
  Card,
  Divider,
  Shadow,
  Typography,
  StyledDivBreadcrumbs,
} from './VoucherDetail.style';
import {
  getVoucherDetail,
  updateVoucher,
  viewSourceHtmlEditor,
  ticketUsageTypeRadioOptions,
  TicketUsageTypeEnums,
  limitQuantity,
  TicketAddNewDefaultParams,
} from '../../../utils/voucher/VoucherUtils';
import {getIslanderPartners} from '../../../utils/islander-partners/IslanderPartners';
import {getVoucherCategory} from '../../../utils/voucher-categories/voucher-categories';
import {rbacPermissions} from '../../../constants/roleBasedAccessControlConstants';
const radioVisibilityOptions = [
  {
    key: 'Public',
    value: 0,
  },
  {
    key: 'Targeted',
    value: 1,
  },
];

interface DateTimeRange {
  startOfDateTime?: Date | null;
  endOfDateTime?: Date | null;
}

function VoucherDetail() {
  const paramsUrl = useParams();
  const location = useLocation();
  const idDetail = paramsUrl?.id;
  const {showNotification, checkRbac} = useAuth();
  const navigate = useNavigate();
  const formatTime = 'DD/MM/YYYY HH:mm';

  // state
  const [isLoading, setIsLoading] = useState(false);
  const [modal, setModal] = useState({
    isModalConfirm: false,
    isModalConfirmDelete: false,
    isModalSuccess: false,
    isModalSuccessDelete: false,
    isModalEditTier: false,
    isModalEditPoints: false,
  });

  const [optionIslandPartners, setOptionIslandPartners] = useState([]);
  const [optionVoucherCategory, setOptionVoucherCategory] = useState([]);
  const [valueMultipleSelectCheckbox, setValueMultipleSelectCheckbox] =
    useState([]);
  const [categorySelectCheckbox, setCategorySelectCheckbox] = useState([]);
  const selectIPPlaceholder = useMemo(() => {
    return Array.isArray(valueMultipleSelectCheckbox) &&
      valueMultipleSelectCheckbox.length > 0
      ? 'Select IP'
      : 'All IP';
  }, [valueMultipleSelectCheckbox]);
  const selectCategoryPlaceholder = useMemo(() => {
    return Array.isArray(categorySelectCheckbox) &&
      categorySelectCheckbox.length > 0
      ? 'Select Categories'
      : 'All';
  }, [categorySelectCheckbox]);

  const [initialValues, setInitialValues] = useState(TicketAddNewDefaultParams);

  const [disabledQty, setDisabledQty] = useState(false);
  const [defaultValue, setDefaultValue] = useState('');
  const [voucherImages, setVoucherImages] = useState([]);

  const onUpdate = async values => {
    setIsLoading(true);
    const payload = {
      ...values,
      merchantIds: valueMultipleSelectCheckbox.map(item => item.value),
      ticketCategoryIds: categorySelectCheckbox.map(item => item.value),
      image: voucherImages.length > 0 ? undefined : '',
    };
    const res = await updateVoucher(idDetail, payload, voucherImages);
    if (res?.success && res?.code === 200) {
      setIsLoading(false);
      showNotification({
        message: res.message,
      });
    } else {
      setIsLoading(false);
      showNotification({
        message: res.message,
        variation: 'error',
      });
    }
  };

  const onSubmit = values => {
    if (
      !checkRbac({permissionString: rbacPermissions.VOUCHERS__LISTING__EDIT})
    ) {
      return;
    }
    onUpdate({
      ...values,
      expireStartDateTime: formatDateTime(values.startOfDateTime),
      expireEndDateTime: formatDateTime(values.endOfDateTime),
      isCashoutAllowed: values.isCashoutAllowed ? 1 : 0,
    });
  };

  const toggleModalConfirm = () => {
    setModal(preState => ({
      ...preState,
      isModalConfirm: !modal.isModalConfirm,
    }));
  };

  const toggleModalConfirmDelete = () => {
    setModal(preState => ({
      ...preState,
      isModalConfirmDelete: !modal.isModalConfirmDelete,
    }));
  };

  const toggleModalSuccess = () => {
    toggleModalConfirm();
    setModal(preState => ({
      ...preState,
      isModalSuccess: !modal.isModalSuccess,
    }));
  };

  const toggleModalSuccessDelete = () => {
    setIsLoading(true);
    new VoucherService().softDelete(idDetail).then(res => {
      if (res && res?.success) {
        setIsLoading(false);
        toggleModalConfirmDelete();
        setModal(preState => ({
          ...preState,
          isModalSuccessDelete: !modal.isModalSuccessDelete,
        }));
      }
    });
  };

  const getDataDetail = async () => {
    if (idDetail) {
      setIsLoading(true);
      await handleGetVoucherCategory();
      const islandPartners = await handleGetIslanderPartners();
      const res = await getVoucherDetail(idDetail);
      if (res && res.data && res.data.data) {
        const data = res?.data?.data;
        setInitialValues({
          title: data?.title,
          usageType: data?.usageType,
          description: data?.description,
          excerpt: data?.excerpt,
          image: data?.image,
          cost: data?.cost,
          amountOffset: data?.amountOffset,
          onlinePurchaseURL: data?.onlinePurchaseURL || '',
          quantity: data?.quantity,
          unlimitedQuantity: data?.unlimitedQuantity,
          usageCount: data?.usageCount,
          redemptionCount: data?.redemptionCount,
          priority: data?.priority,
          orderFeaturedTicket: data?.orderFeaturedTicket,
          isExclusive: data?.isExclusive ? 1 : 0,
          isCashoutAllowed: data?.isCashoutAllowed,
          targetedDistribution: data?.targetedDistribution ? 1 : 0,
          ticketUrl: data?.ticketUrl,
          quantityMaxPerRedemption: data?.quantityMaxPerRedemption ?? 1,
          endOfDateTime: data?.expireEndDateTime ?? null,
          startOfDateTime: data?.expireStartDateTime ?? null,
        });
        setValueMultipleSelectCheckbox(
          islandPartners.filter(f => data.merchantIds.includes(f.value))
        );
        setCategorySelectCheckbox(
          data?.ticketCategories?.map(item => {
            return {title: item.title, value: item.id};
          })
        );
        if (data?.image) {
          setVoucherImages([data?.image]);
        }
        const contentHTML = convertFromHTML(data?.description);
        const state = ContentState.createFromBlockArray(
          contentHTML.contentBlocks,
          contentHTML.entityMap
        );
        setDefaultValue(JSON.stringify(convertToRaw(state)));
        setIsLoading(false);
      }
    }
  };

  const onClickSourceEditor = (data: string) => {
    const res = viewSourceHtmlEditor(data, defaultValue);
    setDefaultValue(res);
  };

  const handleGetIslanderPartners = async () => {
    const res = await getIslanderPartners();
    setOptionIslandPartners(res);
    return res;
  };

  const handleGetVoucherCategory = async () => {
    const res = await getVoucherCategory();
    setOptionVoucherCategory(res);
  };

  const handleCopyClick = (textToCopy: string) => {
    navigator.clipboard.writeText(textToCopy).then(() => {
      showNotification({
        message: 'Link copied!',
      });
    });
  };

  useEffect(() => {
    setDisabledQty(initialValues.unlimitedQuantity);
  }, [initialValues.unlimitedQuantity]);

  useEffect(() => {
    setVoucherImages([]);
    getDataDetail();
  }, []);

  return (
    <>
      <Helmet title="Edit Administrator Detail" />

      <AllModal
        isModalConfirm={modal.isModalConfirm}
        toggleModalConfirm={toggleModalConfirm}
        isModalConfirmDelete={modal.isModalConfirmDelete}
        isModalSuccessDelete={modal.isModalSuccessDelete}
        toggleModalConfirmDelete={toggleModalConfirmDelete}
        toggleModalSuccessDelete={toggleModalSuccessDelete}
        toggleModalSuccess={toggleModalSuccess}
        isModalSuccess={modal.isModalSuccess}
        setModal={setModal}
      />

      <Box
        component="section"
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Typography variant="h3" gutterBottom display="inline">
          {idDetail ? 'Voucher Detail' : 'Add New Voucher'}
        </Typography>

        <StyledDivBreadcrumbs>
          <Breadcrumbs
            routes={[
              typeRoutes(
                'Voucher',
                checkRbac({
                  permissionString: rbacPermissions.VOUCHERS__LISTING__VIEW,
                })
                  ? `/${path.VOUCHER}?${queryString.stringify(location?.state)}`
                  : undefined
              ),
              typeRoutes('Detail', undefined, true),
            ]}
          />
        </StyledDivBreadcrumbs>

        <Divider my={6} />

        <Grid container spacing={6} mb={4}>
          <Grid item xs={12} sm={12} md={6} lg={6} xl>
            <Stats
              title="Use Voucher Count"
              amount={initialValues.usageCount}
              icon={images.icon_points}
              isManage={false}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={6} lg={6} xl>
            <Stats
              title="Get Voucher Count"
              amount={initialValues.redemptionCount}
              icon={images.icon_transactional}
              isManage={false}
            />
          </Grid>
        </Grid>

        <Grid container mb="30px">
          <Grid item xs={12} lg={12}>
            <Shadow>
              <Formik
                initialValues={initialValues}
                enableReinitialize
                validationSchema={Yup.object().shape({
                  title: Yup.string()
                    .required('Voucher Title is required')
                    .max(40),
                  excerpt: Yup.string()
                    .required('Voucher Short Description is required')
                    .max(85),
                  onlinePurchaseURL: Yup.string()
                    .matches(
                      /(https:\/\/|http:\/\/|mailto:)+[a-zA-Z0-9-_]/,
                      'URL is invalid'
                    )
                    .notRequired(),
                  description: Yup.string().required('T&Cs is required'),
                  quantity: Yup.number()

                    .integer()
                    .moreThan(
                      initialValues.unlimitedQuantity ? -2 : -1,
                      'Quantity must be greater than or equal to 0'
                    ),
                  quantityMaxPerRedemption: Yup.number().when('usageType', {
                    is: 2,
                    then: Yup.number()
                      .required()
                      .max(
                        limitQuantity,
                        `For unlimited redemptions, please set limit at ${limitQuantity}.`
                      ),
                    otherwise: Yup.number().nullable(),
                  }),
                  amountOffset: Yup.number().when('isCashoutAllowed', {
                    is: true,
                    then: Yup.number().min(
                      1,
                      'To allow reimbursement, voucher cash value cannot be zero.'
                    ),
                  }),
                  startOfDateTime: Yup.date()
                    .nullable()
                    .required('Start date and time is required')
                    .test(
                      'is-before-end',
                      'Start Date Time must be earlier than End Date Time. Try again.',
                      function (value) {
                        const {endOfDateTime} = this.parent;
                        return !endOfDateTime || value <= endOfDateTime;
                      }
                    ),
                  endOfDateTime: Yup.date()
                    .nullable()
                    .required('End date and time is required')
                    .test(
                      'is-after-start',
                      'End Date Time must be later than Start Date Time. Try again.',
                      function (value) {
                        const {startOfDateTime} = this.parent;
                        return !startOfDateTime || value >= startOfDateTime;
                      }
                    ),
                })}
                onSubmit={onSubmit}
              >
                {({
                  errors,
                  handleBlur,
                  handleChange,
                  handleSubmit,
                  setFieldValue,
                  touched,
                  values,
                }) => {
                  return (
                    <form noValidate onSubmit={handleSubmit}>
                      <Card px={6} pt={6}>
                        <CardContent>
                          <Grid
                            container
                            rowSpacing={0}
                            columnSpacing={10}
                            mb="50px"
                          >
                            <Grid item md={12}>
                              <FormControl fullWidth margin="normal">
                                <Typography
                                  variant="body2"
                                  mb={2}
                                  fontWeight="bold"
                                >
                                  Voucher Title (40 Max Char.){' '}
                                  <span className="mandatory-label">(*)</span>
                                </Typography>
                                <Input
                                  placeholder="Enter Voucher Title"
                                  size="small"
                                  name="title"
                                  value={values.title}
                                  variant="outlined"
                                  helperText={touched.title && errors.title}
                                  onBlur={handleBlur}
                                  onChange={e => {
                                    handleChange(e);
                                  }}
                                  error={Boolean(touched.title && errors.title)}
                                />
                              </FormControl>
                            </Grid>
                            <Grid item md={12}>
                              <FormControl fullWidth margin="normal">
                                <Typography
                                  variant="body2"
                                  mb={2}
                                  fontWeight="bold"
                                >
                                  Voucher Short Description (85 Max Char.){' '}
                                  <span className="mandatory-label">(*)</span>
                                </Typography>
                                <TextArea
                                  label=""
                                  placeholder="Enter Voucher Short Description"
                                  maxRows={2}
                                  name="excerpt"
                                  value={values.excerpt}
                                  helperText={touched.excerpt && errors.excerpt}
                                  onBlur={handleBlur}
                                  onChange={e => {
                                    handleChange(e);
                                  }}
                                  error={Boolean(
                                    touched.excerpt && errors.excerpt
                                  )}
                                />
                              </FormControl>
                            </Grid>
                            <Grid item md={3}>
                              <FormControl fullWidth margin="normal">
                                <Typography
                                  variant="body2"
                                  mb={2}
                                  fontWeight="bold"
                                >
                                  Points Required
                                </Typography>
                                <Input
                                  placeholder="Enter Points"
                                  size="small"
                                  name="cost"
                                  type="number"
                                  value={values.cost}
                                  variant="outlined"
                                  helperText={touched.cost && errors.cost}
                                  onBlur={handleBlur}
                                  onChange={e => {
                                    handleChange(e);
                                  }}
                                  error={Boolean(touched.cost && errors.cost)}
                                />
                              </FormControl>
                            </Grid>
                            <Grid item md={3}>
                              <FormControl fullWidth margin="normal">
                                <Typography
                                  variant="body2"
                                  mb={2}
                                  fontWeight="bold"
                                >
                                  Voucher Cash Value
                                </Typography>
                                <Input
                                  placeholder="Enter Amount Used"
                                  size="small"
                                  name="amountOffset"
                                  type="number"
                                  value={values.amountOffset}
                                  variant="outlined"
                                  helperText={
                                    touched.amountOffset && errors.amountOffset
                                  }
                                  onBlur={handleBlur}
                                  onChange={e => {
                                    handleChange(e);
                                  }}
                                  error={Boolean(
                                    touched.amountOffset && errors.amountOffset
                                  )}
                                />
                              </FormControl>
                            </Grid>
                            <Grid item md={2}>
                              <FormControl fullWidth margin="normal">
                                <div>
                                  <Typography variant="body2" fontWeight="bold">
                                    Allow reimbursement
                                  </Typography>
                                  <Checkbox
                                    sx={{
                                      marginLeft: -3,
                                    }}
                                    checked={
                                      values.isCashoutAllowed ? true : false
                                    }
                                    name="isCashoutAllowed"
                                    value={values.isCashoutAllowed}
                                    onChange={(e, newValue) => {
                                      setFieldValue(
                                        'isCashoutAllowed',
                                        newValue
                                      );
                                    }}
                                    inputProps={{'aria-label': 'controlled'}}
                                  />
                                </div>
                              </FormControl>
                            </Grid>
                            <Grid item md={2.5}>
                              <FormControl fullWidth margin="normal">
                                <Typography
                                  variant="body2"
                                  mb={2}
                                  fontWeight="bold"
                                >
                                  Qty
                                </Typography>
                                <Input
                                  placeholder="Enter Usable Quantity"
                                  size="small"
                                  name="quantity"
                                  type="number"
                                  value={values.quantity}
                                  variant="outlined"
                                  helperText={
                                    touched.quantity && errors.quantity
                                  }
                                  onBlur={handleBlur}
                                  onChange={e => {
                                    handleChange(e);
                                  }}
                                  error={Boolean(
                                    touched.quantity && errors.quantity
                                  )}
                                  disabled={disabledQty}
                                />
                              </FormControl>
                            </Grid>
                            <Grid item md={1.5}>
                              <FormControl fullWidth margin="normal">
                                <div>
                                  <Typography variant="body2" fontWeight="bold">
                                    Unlimited Qty
                                  </Typography>
                                  <Checkbox
                                    sx={{
                                      paddingLeft: 0,
                                    }}
                                    checked={values.unlimitedQuantity}
                                    name="unlimitedQuantity"
                                    value={values.unlimitedQuantity}
                                    onChange={e => {
                                      handleChange(e);
                                      setInitialValues({
                                        ...values,
                                        quantity: e.target.checked ? -1 : 0,
                                        unlimitedQuantity: e.target.checked,
                                      });
                                    }}
                                    inputProps={{'aria-label': 'controlled'}}
                                  />
                                </div>
                              </FormControl>
                            </Grid>
                            <Grid item md={12}>
                              <FormControl fullWidth margin="normal">
                                <Typography
                                  variant="body2"
                                  mb={2}
                                  fontWeight="bold"
                                >
                                  T&amp;Cs{' '}
                                  <span className="mandatory-label">(*)</span>
                                </Typography>
                                <RichTextEditor
                                  label="Enter T&amp;Cs"
                                  defaultValue={defaultValue}
                                  helperText={
                                    touched.description && errors.description
                                  }
                                  error={Boolean(
                                    touched.description && errors.description
                                  )}
                                  onChange={e => {
                                    let stringContent = '';
                                    e.getCurrentContent()
                                      .getBlocksAsArray()
                                      .map(
                                        item =>
                                          (stringContent =
                                            stringContent + item.text)
                                      );
                                    let content =
                                      stringContent.search('</') === -1
                                        ? stateToHTML(e.getCurrentContent())
                                        : stringContent;
                                    content = content.replace(
                                      '<strong>',
                                      '<b>'
                                    );
                                    content = content.replace(
                                      '</strong>',
                                      '</b>'
                                    );
                                    const regex = /href="(?!https:\/\/|tel:)/g;
                                    handleChange(
                                      content === '<p><br></p>'
                                        ? ''
                                        : content.replace(
                                            regex,
                                            'href="https://'
                                          )
                                    );
                                    setInitialValues({
                                      ...values,
                                      description:
                                        content === '<p><br></p>'
                                          ? ''
                                          : content.replace(
                                              regex,
                                              'href="https://'
                                            ),
                                    });
                                  }}
                                  controls={[
                                    'title',
                                    'bold',
                                    'italic',
                                    'underline',
                                    'strikethrough',
                                    'undo',
                                    'redo',
                                    'link',
                                    'media',
                                    'numberList',
                                    'bulletList',
                                    'source',
                                  ]}
                                  customControls={[
                                    {
                                      name: 'source',
                                      icon: <SourceOutlinedIcon />,
                                      type: 'callback',
                                      onClick: () => {
                                        onClickSourceEditor(
                                          initialValues?.description
                                        );
                                      },
                                    },
                                  ]}
                                />
                              </FormControl>
                            </Grid>
                            <Grid item md={12}>
                              <FormControl fullWidth margin="dense">
                                <Typography
                                  variant="body2"
                                  mb={2}
                                  fontWeight="bold"
                                >
                                  Online Purchase URL
                                </Typography>
                                <Input
                                  type="text"
                                  name="onlinePurchaseURL"
                                  placeholder="Enter Online Purchase URL"
                                  size="small"
                                  variant="outlined"
                                  value={values.onlinePurchaseURL}
                                  onChange={e => {
                                    handleChange(e);
                                  }}
                                  helperText={
                                    touched.onlinePurchaseURL &&
                                    errors.onlinePurchaseURL
                                  }
                                  onBlur={handleBlur}
                                  error={Boolean(
                                    touched.onlinePurchaseURL &&
                                      errors.onlinePurchaseURL
                                  )}
                                />
                              </FormControl>
                            </Grid>
                            <Grid item md={6} mb={2}>
                              <FormControl variant="standard" fullWidth>
                                <Typography
                                  variant="body2"
                                  mb={2}
                                  mt={3}
                                  fontWeight="bold"
                                >
                                  Start Date Time{' '}
                                </Typography>
                                <DateTimePicker
                                  value={values.startOfDateTime}
                                  helperText={errors.startOfDateTime}
                                  isErrorMessage={Boolean(
                                    touched.startOfDateTime &&
                                      errors.startOfDateTime
                                  )}
                                  maxDate={values?.endOfDateTime || undefined}
                                  onChange={e => {
                                    setFieldValue('startOfDateTime', e ?? null);
                                  }}
                                />
                              </FormControl>
                            </Grid>
                            <Grid item md={6} mb={2}>
                              <FormControl variant="standard" fullWidth>
                                <Typography
                                  variant="body2"
                                  mb={2}
                                  mt={3}
                                  fontWeight="bold"
                                >
                                  End Date Time{' '}
                                </Typography>
                                <DateTimePicker
                                  disabled={!values.startOfDateTime}
                                  value={values.endOfDateTime}
                                  helperText={errors.endOfDateTime}
                                  isErrorMessage={Boolean(
                                    touched.endOfDateTime &&
                                      errors.endOfDateTime
                                  )}
                                  minDate={values?.startOfDateTime || undefined}
                                  onChange={e => {
                                    setFieldValue('endOfDateTime', e ?? null);
                                  }}
                                />
                              </FormControl>
                            </Grid>
                            <Grid item md={6}>
                              <FormControl variant="standard" fullWidth>
                                <Typography
                                  variant="body2"
                                  mb={2}
                                  mt={3}
                                  fontWeight="bold"
                                >
                                  Select IP
                                </Typography>
                                <MultipleSelectCheckbox
                                  placeholder={selectIPPlaceholder}
                                  value={valueMultipleSelectCheckbox}
                                  onChange={(e, values) => {
                                    setValueMultipleSelectCheckbox(values);
                                  }}
                                  options={optionIslandPartners}
                                />
                              </FormControl>
                            </Grid>
                            <Grid item md={6}>
                              <FormControl variant="standard" fullWidth>
                                <Typography
                                  variant="body2"
                                  mb={2}
                                  mt={3}
                                  fontWeight="bold"
                                >
                                  Voucher Category
                                </Typography>
                                <MultipleSelectCheckbox
                                  placeholder={selectCategoryPlaceholder}
                                  value={categorySelectCheckbox}
                                  onChange={(e, values) => {
                                    setCategorySelectCheckbox(values);
                                  }}
                                  options={optionVoucherCategory}
                                />
                              </FormControl>
                            </Grid>
                            <Grid item md={6}>
                              <FormControl variant="standard" fullWidth>
                                <Typography
                                  variant="body2"
                                  mb={2}
                                  mt={3}
                                  fontWeight="bold"
                                >
                                  Usage Type
                                </Typography>
                                <RadioGroups
                                  name="usageType"
                                  options={Object.values(
                                    ticketUsageTypeRadioOptions
                                  )
                                    .filter(
                                      item =>
                                        item.value !==
                                        TicketUsageTypeEnums.DEFAULT
                                    )
                                    .sort(
                                      (a, b) =>
                                        [
                                          TicketUsageTypeEnums.SINGLE_NON_REPEATABLE,
                                          TicketUsageTypeEnums.SINGLE_REPEATABLE,
                                          TicketUsageTypeEnums.MULTI_REDEMPTION,
                                        ].indexOf(a.value) -
                                        [
                                          TicketUsageTypeEnums.SINGLE_NON_REPEATABLE,
                                          TicketUsageTypeEnums.SINGLE_REPEATABLE,
                                          TicketUsageTypeEnums.MULTI_REDEMPTION,
                                        ].indexOf(b.value)
                                    )}
                                  values={values.usageType}
                                  onChange={e => {
                                    handleChange(e);
                                    const quantityMaxPerRedemption =
                                      +e.target.value ===
                                      TicketUsageTypeEnums.MULTI_REDEMPTION
                                        ? 1
                                        : null;
                                    setFieldValue(
                                      'quantityMaxPerRedemption',
                                      quantityMaxPerRedemption
                                    );
                                    setInitialValues({
                                      ...values,
                                      usageType: +e.target.value,
                                      quantityMaxPerRedemption:
                                        quantityMaxPerRedemption,
                                    });
                                  }}
                                />
                              </FormControl>
                            </Grid>
                            <Grid item md={6}>
                              <FormControl variant="standard" fullWidth>
                                <Typography
                                  variant="body2"
                                  mb={2}
                                  mt={3}
                                  fontWeight="bold"
                                >
                                  Visibility
                                </Typography>
                                <RadioGroups
                                  name="targetedDistribution"
                                  options={radioVisibilityOptions}
                                  values={values.targetedDistribution}
                                  onChange={e => {
                                    setFieldValue(
                                      'targetedDistribution',
                                      +e.target.value
                                    );
                                  }}
                                />
                              </FormControl>
                            </Grid>
                            {values.usageType ===
                              TicketUsageTypeEnums.MULTI_REDEMPTION && (
                              <Grid item md={12} mt={4}>
                                <FormControl variant="standard" fullWidth>
                                  <Typography
                                    variant="body2"
                                    mb={2}
                                    fontWeight="bold"
                                  >
                                    Maximum quantity per redemption transaction{' '}
                                    <span className="mandatory-label">(*)</span>
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    fontSize="11px"
                                    marginTop="-4px"
                                  >
                                    For unlimited redemptions, please set limit
                                    at {limitQuantity}.
                                  </Typography>
                                  <Input
                                    className="helperText__unsetMarginLeft short-field"
                                    placeholder="e.g. 3"
                                    type="number"
                                    size="small"
                                    name="quantityMaxPerRedemption"
                                    value={values.quantityMaxPerRedemption}
                                    variant="outlined"
                                    helperText={
                                      touched.quantityMaxPerRedemption &&
                                      errors.quantityMaxPerRedemption
                                    }
                                    onChange={e => {
                                      if (e.target.value <= 0) return;
                                      handleChange(e);
                                    }}
                                    error={Boolean(
                                      touched.quantityMaxPerRedemption &&
                                        errors.quantityMaxPerRedemption
                                    )}
                                  />
                                </FormControl>
                              </Grid>
                            )}
                            <Grid item xs={5}>
                              <FormControl variant="standard" fullWidth>
                                <Typography
                                  variant="body2"
                                  mb={2}
                                  mt={3}
                                  fontWeight="bold"
                                >
                                  Voucher Image
                                </Typography>
                                <AspectSelectImage
                                  defaultImages={voucherImages}
                                  setDefaultImages={setVoucherImages}
                                />
                              </FormControl>
                            </Grid>
                            <Grid item xs={12} />
                            <Grid item marginLeft="-12px">
                              <Tooltips
                                title="When this is checked, it will bump the voucher to the top of the voucher listing page in B2C"
                                arrow={false}
                              >
                                <FormControl fullWidth margin="dense">
                                  <div
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                    }}
                                  >
                                    <Checkbox
                                      name="priority"
                                      checked={values.priority}
                                      value={values.priority}
                                      onChange={e => {
                                        handleChange(e);
                                        setInitialValues({
                                          ...values,
                                          priority: e.target.checked,
                                        });
                                      }}
                                      inputProps={{'aria-label': 'controlled'}}
                                    />
                                    <Typography
                                      variant="body2"
                                      fontWeight="bold"
                                    >
                                      Priority
                                    </Typography>
                                  </div>
                                  <Typography
                                    variant="caption"
                                    fontSize="11px"
                                    marginTop="-4px"
                                    marginLeft="41px"
                                  >
                                    Bump voucher to the top of listing in B2C
                                    Web
                                  </Typography>
                                </FormControl>
                              </Tooltips>
                            </Grid>
                            <Grid item marginLeft="-12px">
                              <FormControl fullWidth margin="dense">
                                <div
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                  }}
                                >
                                  <Checkbox
                                    checked={values.isExclusive ? true : false}
                                    name="isExclusive"
                                    value={values.isExclusive}
                                    onChange={e => {
                                      handleChange(e);
                                      setInitialValues({
                                        ...values,
                                        isExclusive: e.target.checked ? 1 : 0,
                                      });
                                    }}
                                    inputProps={{'aria-label': 'controlled'}}
                                  />
                                  <Typography variant="body2" fontWeight="bold">
                                    Exclusive
                                  </Typography>
                                </div>
                                <Typography
                                  variant="caption"
                                  fontSize="11px"
                                  marginLeft="41px"
                                  marginTop="-4px"
                                >
                                  Voucher cannot be used with any other vouchers
                                </Typography>
                              </FormControl>
                            </Grid>
                          </Grid>
                          {values?.ticketUrl !== null && (
                            <>
                              <Divider margin="dense" />
                              <Grid item margin="normal" marginTop={6}>
                                <Typography variant="body2" fontWeight="bold">
                                  Voucher URL
                                </Typography>
                                <a
                                  href={values.ticketUrl}
                                  target="_blank"
                                  style={{
                                    textDecoration: 'none',
                                    color: '#376FD0',
                                  }}
                                >
                                  {values.ticketUrl}
                                </a>
                                <img
                                  onClick={() =>
                                    handleCopyClick(values.ticketUrl)
                                  }
                                  src={images.icon_copy_blue}
                                  style={{
                                    cursor: 'pointer',
                                    display: 'inline-block',
                                    verticalAlign: 'middle',
                                    paddingLeft: '12px',
                                  }}
                                />
                              </Grid>
                            </>
                          )}
                        </CardContent>
                      </Card>
                      {checkRbac(
                        {
                          permissionString: [
                            rbacPermissions.VOUCHERS__LISTING__EDIT,
                            rbacPermissions.VOUCHERS__LISTING__DELETE,
                            rbacPermissions.VOUCHER_TRANSACTIONS__LISTING__VIEW,
                          ],
                        },
                        <FooterDetail
                          canExplicitlyDelete={checkRbac({
                            permissionString:
                              rbacPermissions.VOUCHERS__LISTING__DELETE,
                          })}
                          canExplicitlyAddOrSave={checkRbac({
                            permissionString:
                              rbacPermissions.VOUCHERS__LISTING__EDIT,
                          })}
                          isEdit={idDetail ? true : false}
                          isDelete={checkRbac({
                            permissionString:
                              rbacPermissions.VOUCHERS__LISTING__DELETE,
                          })}
                          toggleModalConfirm={toggleModalConfirm}
                          toggleModalConfirmDelete={toggleModalConfirmDelete}
                          titleDelete="Delete Voucher"
                          loading={isLoading}
                          isVoucher={checkRbac({
                            permissionString:
                              rbacPermissions.VOUCHER_TRANSACTIONS__LISTING__VIEW,
                          })}
                          onClickVoucher={() => {
                            navigate(
                              `/${path.VOUCHER_TRANSACTION_HISTORY}?voucherId=${idDetail}`,
                              {
                                state: {
                                  voucherId: idDetail,
                                },
                              }
                            );
                          }}
                        />
                      )}
                    </form>
                  );
                }}
              </Formik>
            </Shadow>
          </Grid>
        </Grid>
      </Box>
    </>
  );
}

export default VoucherDetail;
